import { z } from 'zod';
import { FeeRate } from './fee-schedule.js';
/**
 * The price forms of the firm's catalogue (ADR-071 §4) as one closed union: every priced position
 * holds exactly one of them. Amounts are integer cents in euros - the currency of every catalogue
 * (`EUR`); a contract keeps the price as it was agreed, whatever the catalogue says later.
 */
/** Integer cents, never negative; up to 100 million euros. */
export const Cents = z.number().int().nonnegative().max(10_000_000_000);
/** The currency of every price of the catalogue. */
export const CatalogCurrency = z.literal('EUR').meta({ id: 'CatalogCurrency' });
/**
 * A key of a position of the catalogue (`fibu.package_basis`): the area in lower case, then the
 * position. Stable across library versions - a contract names the position by it.
 */
export const PositionKey = z
    .string()
    .regex(/^[a-z]+(\.[a-z0-9_]+)+$/, 'Position keys look like fibu.package_basis')
    .max(80)
    .meta({ id: 'PositionKey' });
export const PriceForm = z
    .enum(['fixed', 'time', 'per_unit', 'percent', 'value_fee', 'minimum', 'tiered'])
    .meta({ id: 'PriceForm' });
/** What a fixed price or a minimum fee is charged for: once, or per period. */
export const PricePeriod = z
    .enum(['once', 'month', 'quarter', 'year', 'booking_period'])
    .meta({ id: 'PricePeriod' });
/** What a price per unit or a tier counts (STBV-B1 "STUECK"). */
export const PriceUnit = z
    .enum([
    'employee',
    'payslip',
    'booking_line',
    'tax_return',
    'application',
    'certificate',
    'report',
    'invoice',
    'return_debit',
    'month',
    'day',
    'km',
    'copy',
    'order',
])
    .meta({ id: 'PriceUnit' });
/**
 * How a fixed price follows its basis: not at all, once a year (E2.FIBU.020), or when the
 * basis moves by more than a tolerance (E2.LOHN.030 - number of employees).
 */
export const FixedAdjustment = z
    .discriminatedUnion('kind', [
    z.object({ kind: z.literal('none') }),
    z.object({ kind: z.literal('yearly') }),
    z.object({
        kind: z.literal('basis_change'),
        /** Tolerance in basis points (1000 = 10 %). */
        toleranceBasisPoints: z.number().int().min(1).max(10_000),
    }),
])
    .meta({ id: 'FixedAdjustment' });
/** The time unit a time fee is charged by: every started quarter or half hour. */
export const TimeIncrement = z.enum(['quarter_hour', 'half_hour']).meta({ id: 'TimeIncrement' });
export const timeIncrementMinutes = {
    quarter_hour: 15,
    half_hour: 30,
};
/**
 * Where the value of a value fee comes from: as the provision of the StBVV says, or at least a
 * value the firm sets (minimum value, E2.IND.010).
 */
export const ValueBasis = z
    .discriminatedUnion('basis', [
    z.object({ basis: z.literal('statutory') }),
    z.object({ basis: z.literal('at_least'), minimum: Cents.positive() }),
])
    .meta({ id: 'ValueBasis' });
/** A tenth rate as agreed: the rate itself, or the least rate with a higher one reserved. */
export const RateKind = z.enum(['fixed', 'minimum']).meta({ id: 'RateKind' });
const Tier = z.object({
    /** The highest quantity of the tier; `null` for the last, open one. */
    upTo: z.number().int().positive().nullable(),
    /** Cents per unit while the quantity lies in the tier. */
    amount: Cents,
});
const fixed = z.object({
    form: z.literal('fixed'),
    amount: Cents,
    period: PricePeriod,
    adjustment: FixedAdjustment,
});
const time = z.object({
    form: z.literal('time'),
    /** Cents per started increment. */
    amount: Cents.positive(),
    increment: TimeIncrement,
    /** Increments charged at least. */
    minimumUnits: z.number().int().min(0).max(1000),
});
const perUnit = z.object({ form: z.literal('per_unit'), amount: Cents, unit: PriceUnit });
const percent = z.object({
    form: z.literal('percent'),
    /** The positions the share is taken of - whichever of them the contract holds. */
    of: z.array(PositionKey).min(1).max(20),
    /** Surcharge (positive) or discount (negative) in basis points: 1000 = 10 %. */
    basisPoints: z
        .number()
        .int()
        .min(-10_000)
        .max(100_000)
        .refine((n) => n !== 0, 'A percentage of zero is no price'),
});
const valueFee = z.object({
    form: z.literal('value_fee'),
    /** A value-based provision of the fee schedule in force (`33-1`). */
    provision: z.string().min(1).max(40),
    rate: FeeRate,
    rateKind: RateKind,
    value: ValueBasis,
});
const minimum = z.object({
    form: z.literal('minimum'),
    amount: Cents.positive(),
    period: PricePeriod,
});
const tiered = z.object({
    form: z.literal('tiered'),
    unit: PriceUnit,
    period: PricePeriod,
    tiers: z.array(Tier).min(1).max(20),
});
/** True when every tier but the last has a higher bound than the one before, and the last is open. */
function tiersAscend(tiers) {
    return tiers.every((tier, i) => {
        const last = i === tiers.length - 1;
        if (last)
            return tier.upTo === null;
        const before = i === 0 ? 0 : (tiers[i - 1]?.upTo ?? Number.POSITIVE_INFINITY);
        return tier.upTo !== null && tier.upTo > before;
    });
}
export const Price = z
    .discriminatedUnion('form', [fixed, time, perUnit, percent, valueFee, minimum, tiered])
    .refine((price) => price.form !== 'tiered' || tiersAscend(price.tiers), {
    message: 'Tiers ascend, and only the last one is open',
    path: ['tiers'],
})
    .refine((price) => price.form !== 'percent' || new Set(price.of).size === price.of.length, {
    message: 'Each position once',
    path: ['of'],
})
    .meta({ id: 'Price' });
//# sourceMappingURL=catalog-prices.js.map